import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { API_BASE_URL } from '../../../app.definitions';
import { SYMPTOMS_QUESTION_GROUPS } from '../../../shared/components/charts/symptons-yes-no-bar-chart/definitions/symptoms-question-groups';
import {
  ApiResponse,
  GeneralHealthHistoryData,
  SymptomHistoryData,
  WeightHistoryData,
} from '../../../shared/models';
import { Observable } from 'rxjs';
import { BasicWebkData, GetDataAvailabilityResponse } from 'ngx-esprio-shared';
import { UserScoreResponse } from 'ngx-esprio-shared/lib/shared/models/account/responses/user-score-response.model';

@Injectable()
export class UserDataApiService {
  constructor(@Inject(API_BASE_URL) private apiBaseUrl: string, private httpClient: HttpClient) {}

  public getUserWeightHistoryData(
    userId: string,
    monitoredUserId: string,
    startDate?: Date,
    endDate?: Date
  ): Observable<ApiResponse<WeightHistoryData[]>> {
    const isoStartDate = startDate?.toISOString();
    const isoEndDate = endDate?.toISOString();

    let params = new HttpParams();
    if (isoStartDate) {
      params = params.set('startDate', isoStartDate);
    }
    if (isoEndDate) {
      params = params.set('endDate', isoEndDate);
    }

    return this.httpClient.get<ApiResponse<WeightHistoryData[]>>(
      `${this.apiBaseUrl}/monitoring/${userId}/${monitoredUserId}/history/weight-data`,
      { params }
    );
  }

  public getUserBasicWebkData(
    userId: string,
    monitoredUserId: string,
    startDate?: Date,
    endDate?: Date
  ): Observable<ApiResponse<BasicWebkData[]>> {
    const isoStartDate = startDate?.toISOString();
    const isoEndDate = endDate?.toISOString();

    let params = new HttpParams();
    if (isoStartDate) {
      params = params.set('startDate', isoStartDate);
    }
    if (isoEndDate) {
      params = params.set('endDate', isoEndDate);
    }

    return this.httpClient.get<ApiResponse<BasicWebkData[]>>(
      `${this.apiBaseUrl}/monitoring/${userId}/${monitoredUserId}/history/basic-webk`,
      { params }
    );
  }

  public getGeneralHealthHistoryData(
    userId: string,
    monitoredUserId: string,
    startDate?: Date,
    endDate?: Date
  ): Observable<ApiResponse<GeneralHealthHistoryData[]>> {
    const isoStartDate = startDate?.toISOString();
    const isoEndDate = endDate?.toISOString();

    let params = new HttpParams();
    if (isoStartDate) {
      params = params.set('startDate', isoStartDate);
    }
    if (isoEndDate) {
      params = params.set('endDate', isoEndDate);
    }

    return this.httpClient.get<ApiResponse<GeneralHealthHistoryData[]>>(
      `${this.apiBaseUrl}/monitoring/${userId}/${monitoredUserId}/history/general-health`,
      { params }
    );
  }

  public getSymptomsHistoryData(
    userId: string,
    monitoredUserId: string,
    startDate?: Date,
    endDate?: Date,
    submissionId?: string,
    questionGroup?: SYMPTOMS_QUESTION_GROUPS
  ): Observable<ApiResponse<SymptomHistoryData[]>> {
    const isoStartDate = startDate?.toISOString();
    const isoEndDate = endDate?.toISOString();

    let params = new HttpParams();
    if (isoStartDate) {
      params = params.set('startDate', isoStartDate);
    }
    if (isoEndDate) {
      params = params.set('endDate', isoEndDate);
    }
    if (submissionId) {
      params = params.set('submissionId', submissionId);
    }
    if (questionGroup) {
      params = params.set('questionGroup', questionGroup);
    }

    return this.httpClient.get<ApiResponse<SymptomHistoryData[]>>(
      `${this.apiBaseUrl}/monitoring/${userId}/${monitoredUserId}/history/symptoms`,
      { params }
    );
  }

  public getUserWeightChangePercentage(
    userId: string,
    monitoredUserId: string,
    startDate: Date,
    endDate: Date
  ): Observable<
    ApiResponse<{ weightChangePercentage: number; startDate: string; endDate: string }>
  > {
    return this.httpClient.post<
      ApiResponse<{ weightChangePercentage: number; startDate: string; endDate: string }>
    >(`${this.apiBaseUrl}/users/${userId}/weight-data/change-percentage`, {
      monitoredUserId,
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
    });
  }

  public getUserTimeSnap(
    userId: string,
    monitoredUserId: string,
    referenceDate?: Date
  ): Observable<ApiResponse<WeightHistoryData>> {
    return this.httpClient.post<ApiResponse<WeightHistoryData>>(
      `${this.apiBaseUrl}/users/${userId}/weight-data/timesnap`,
      { monitoredUserId, referenceDate: referenceDate ? referenceDate.toISOString() : undefined }
    );
  }

  public getUserScoreDetails(
    userId: string,
    monitoredUserId: string,
    submissionId?: string
  ): Observable<ApiResponse<UserScoreResponse>> {
    let params = new HttpParams();
    if (submissionId) params = params.set('submissionId', submissionId);
    if (monitoredUserId) params = params.set('monitoredUserId', monitoredUserId);

    return this.httpClient.get<ApiResponse<UserScoreResponse>>(
      `${this.apiBaseUrl}/users/${userId}/submissions/score-details`,
      { params }
    );
  }

  public checkDataAvailability(
    userId: string,
    monitoredUserId: string
  ): Observable<ApiResponse<GetDataAvailabilityResponse>> {
    return this.httpClient.get<ApiResponse<GetDataAvailabilityResponse>>(
      `monitoring/${userId}/${monitoredUserId}/history/data-availability`
    );
  }
}
